import React, { useState } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DownloadQueue = ({ selectedTracks, onClearSelection, onRemoveTrack }) => {
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleBatchDownload = async () => {
    if (selectedTracks.length === 0) return;

    try {
      setDownloading(true);
      setProgress(10);

      const trackIds = selectedTracks.map(t => t.track_id);
      
      setProgress(30);

      const response = await api.post(
        `/downloads/batch`,
        { track_ids: trackIds },
        { 
          withCredentials: true,
          responseType: 'blob',
          onDownloadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(30 + (percentCompleted * 0.7)); // 30% to 100%
          }
        }
      );

      setProgress(100);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `WTPL_Tracks_${new Date().toISOString().slice(0,10)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Clear selection after successful download
      onClearSelection();
    } catch (error) {
      console.error('Error batch downloading:', error);
      alert('Error al descargar los tracks. Intenta de nuevo.');
    } finally {
      setDownloading(false);
      setProgress(0);
    }
  };

  if (selectedTracks.length === 0) return null;

  return (
    <div className="fixed bottom-20 left-0 right-0 bg-gradient-to-r from-[#1A1A1A] to-[#2A2A2A] border-t border-cyan-500/30 p-4 z-40 shadow-2xl animate-slide-up">
      <div className="container mx-auto">
        <div className="flex items-center gap-4">
          {/* Selection Info */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#00D9FF] to-[#0088FF] flex items-center justify-center">
              <span className="text-white font-bold">{selectedTracks.length}</span>
            </div>
            <div>
              <p className="text-white font-medium">
                {selectedTracks.length} track{selectedTracks.length !== 1 ? 's' : ''} seleccionado{selectedTracks.length !== 1 ? 's' : ''}
              </p>
              <p className="text-gray-400 text-sm">
                Listo para descargar
              </p>
            </div>
          </div>

          {/* Selected Tracks Preview */}
          <div className="hidden md:flex items-center gap-2 flex-1 overflow-x-auto px-4">
            {selectedTracks.slice(0, 5).map((track) => (
              <Badge 
                key={track.track_id}
                className="bg-[#252525] text-gray-300 border-gray-600 whitespace-nowrap flex items-center gap-1 cursor-pointer hover:bg-red-500/20 hover:border-red-500 transition-colors"
                onClick={() => onRemoveTrack(track.track_id)}
              >
                {track.title}
                <span className="text-red-400 ml-1">✕</span>
              </Badge>
            ))}
            {selectedTracks.length > 5 && (
              <Badge className="bg-gray-700 text-gray-300">
                +{selectedTracks.length - 5} más
              </Badge>
            )}
          </div>

          {/* Progress Bar (when downloading) */}
          {downloading && (
            <div className="flex-1 mx-4">
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#00D9FF] to-[#0088FF] transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-1 text-center">
                Preparando ZIP... {Math.round(progress)}%
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={onClearSelection}
              variant="outline"
              className="text-gray-400 hover:text-white border-gray-600"
              disabled={downloading}
            >
              Limpiar
            </Button>
            <Button
              onClick={handleBatchDownload}
              disabled={downloading}
              className="bg-gradient-to-r from-[#00D9FF] to-[#0088FF] hover:opacity-90 font-bold"
              data-testid="batch-download-btn"
            >
              {downloading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  Descargando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  📥 Descargar Todo ({selectedTracks.length})
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadQueue;
